<template>
	<div class="p-6">
		<div class="flex justify-between items-start mb-6">
			<div>
				<h1 class="text-2xl font-semibold text-gray-800 m-0">Bulk Discounts</h1>
				<p class="text-gray-500 mt-1 mb-0">Manage quantity-based discount tiers</p>
			</div>
			<button
				class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors"
				@click="openCreateModal"
			>
				<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
				</svg>
				Create New
			</button>
		</div>

		<div class="bg-white rounded-lg shadow-sm overflow-hidden">
			<div class="py-4 px-6 border-b border-gray-200">
				<div class="flex flex-wrap items-center justify-between gap-4">
					<div class="flex items-center gap-2">
						<span class="font-semibold text-gray-700">Bulk Discounts</span>
						<span class="bg-orange-2 text-orange-7 py-0.5 px-2 rounded-full text-xs font-medium">
							{{ filteredBulkDiscounts.length }} total
						</span>
					</div>
					<div class="flex items-center gap-3">
						<div class="relative">
							<svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
							</svg>
							<input
								v-model="searchQuery"
								type="text"
								placeholder="Search by name..."
								class="py-2 px-3 pl-9 border border-gray-300 rounded-md text-sm w-[200px] outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
							>
						</div>
						<button
							class="inline-flex items-center gap-1.5 py-2 px-3 bg-white border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer"
							@click="refreshData"
						>
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
							</svg>
							Refresh
						</button>
					</div>
				</div>
			</div>

			<div v-if="loading" class="flex justify-center items-center py-12">
				<div class="w-8 h-8 border-2 border-gray-200 border-t-orange-7 rounded-full animate-spin"></div>
			</div>

			<div v-else-if="filteredBulkDiscounts.length === 0" class="text-center py-12 text-gray-500">
				<svg class="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
				</svg>
				<p class="m-0">No bulk discounts found</p>
			</div>

			<table v-else class="w-full border-collapse">
				<thead>
					<tr class="bg-gray-50 border-b border-gray-200">
						<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
						<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
						<th class="py-3 px-6 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Count Type</th>
						<th class="py-3 px-6 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Active</th>
						<th class="py-3 px-6 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="discount in paginatedBulkDiscounts"
						:key="discount.id"
						class="border-b border-gray-200 transition-colors hover:bg-gray-50"
					>
						<td class="py-4 px-6 text-sm text-gray-500">{{ discount.id }}</td>
						<td class="py-4 px-6 text-sm font-medium text-gray-900">{{ discount.name }}</td>
						<td class="py-4 px-6 text-center">
							<span
								class="inline-flex items-center py-0.5 px-2 rounded-full text-xs font-medium"
								:class="discount.count === 'pages' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'"
							>
								{{ discount.count }}
							</span>
						</td>
						<td class="py-4 px-6 text-center">
							<span
								v-if="discount.active === 1"
								class="inline-flex items-center gap-1 py-0.5 px-2 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium"
							>
								<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
									<path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
								</svg>
								Active
							</span>
							<span v-else class="text-xs text-gray-400">Inactive</span>
						</td>
						<td class="py-4 px-6 text-right">
							<div class="flex items-center justify-end gap-1">
								<button
									class="p-1.5 bg-transparent border-none text-orange-7 cursor-pointer rounded transition-colors hover:bg-orange-2"
									title="Edit bulk discount"
									@click="editBulkDiscount(discount)"
								>
									<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
									</svg>
								</button>
								<button
									class="p-1.5 bg-transparent border-none text-red-500 cursor-pointer rounded transition-colors hover:bg-red-50"
									title="Delete bulk discount"
									@click="confirmDeleteBulkDiscount(discount)"
								>
									<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
									</svg>
								</button>
							</div>
						</td>
					</tr>
				</tbody>
			</table>

			<div v-if="filteredBulkDiscounts.length > 0" class="flex flex-wrap items-center justify-between py-3 px-6 border-t border-gray-200 gap-4">
				<div class="flex items-center gap-2">
					<span class="text-sm text-gray-500">Rows per page:</span>
					<select
						v-model="pageSize"
						class="py-1 px-2 border border-gray-300 rounded text-sm outline-none"
					>
						<option :value="10">10</option>
						<option :value="25">25</option>
						<option :value="50">50</option>
					</select>
				</div>
				<div class="flex items-center gap-2">
					<span class="text-sm text-gray-500">
						{{ (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage * pageSize, filteredBulkDiscounts.length) }} of {{ filteredBulkDiscounts.length }}
					</span>
					<button
						:disabled="currentPage === 1"
						class="p-1.5 border border-gray-300 rounded bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
						@click="currentPage--"
					>
						<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
						</svg>
					</button>
					<button
						:disabled="currentPage >= totalPages"
						class="p-1.5 border border-gray-300 rounded bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
						@click="currentPage++"
					>
						<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
						</svg>
					</button>
				</div>
			</div>
		</div>

		<div
			v-if="showModal"
			class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
			@click.self="closeModal"
		>
			<div class="bg-white rounded-lg p-6 w-full max-w-[400px] shadow-2xl">
				<h3 class="text-lg font-semibold text-gray-900 m-0 mb-4">Create New Bulk Discount</h3>
				<div class="mb-4">
					<label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
					<input
						v-model="newBulkDiscount.name"
						type="text"
						placeholder="Enter discount name"
						class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
					>
				</div>
				<div class="flex justify-end gap-3">
					<button
						class="py-2 px-4 bg-white border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer"
						@click="closeModal"
					>
						Cancel
					</button>
					<button
						:disabled="!newBulkDiscount.name || saving"
						class="py-2 px-4 bg-orange-7 text-white border-none rounded-md text-sm font-medium cursor-pointer disabled:opacity-50"
						@click="createBulkDiscount"
					>
						{{ saving ? 'Creating...' : 'Create' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { BulkModel } from '@sosocio/creator/interfaces';
import BulkService from 'services/BulkService';
import Swal from 'sweetalert2';

const showToast = (message: string, icon: 'success' | 'error' = 'success') => {
	Swal.fire({
		toast: true,
		position: 'top-end',
		icon,
		title: message,
		showConfirmButton: false,
		timer: 3000,
		timerProgressBar: true,
		customClass: {
			popup: 'windmill-swal-toast',
		},
	});
};

export default defineComponent({
	name: 'BulkDiscountsPage',
	setup() {
		const router = useRouter();
		
		const bulkDiscounts = ref<BulkModel[]>([]);
		const loading = ref(false);
		const saving = ref(false);
		const showModal = ref(false);
		const searchQuery = ref('');
		const currentPage = ref(1);
		const pageSize = ref(10);
		
		const newBulkDiscount = ref({
			name: '',
		});
		
		const filteredBulkDiscounts = computed(() => {
			if (!searchQuery.value) return bulkDiscounts.value;
			const query = searchQuery.value.toLowerCase();
			return bulkDiscounts.value.filter(discount =>
				discount.name.toLowerCase().includes(query)
			);
		});
		
		const totalPages = computed(() => Math.ceil(filteredBulkDiscounts.value.length / pageSize.value));
		
		const paginatedBulkDiscounts = computed(() => {
			const start = (currentPage.value - 1) * pageSize.value;
			return filteredBulkDiscounts.value.slice(start, start + pageSize.value);
		});
		
		const fetchData = async () => {
			loading.value = true;
			try {
				const params = new URLSearchParams({ limit: '0' });
				bulkDiscounts.value = await BulkService.getAll(params);
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load bulk discounts',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			} finally {
				loading.value = false;
			}
		};
		
		const refreshData = () => {
			fetchData();
		};
		
		const openCreateModal = () => {
			newBulkDiscount.value = { name: '' };
			showModal.value = true;
		};
		
		const closeModal = () => {
			showModal.value = false;
		};
		
		const createBulkDiscount = async () => {
			if (!newBulkDiscount.value.name) return;
			saving.value = true;
			try {
				const created = await BulkService.create({
					name: newBulkDiscount.value.name,
					count: 'pages',
					active: 1,
				});
				bulkDiscounts.value.unshift(created);
				closeModal();
				showToast('Bulk discount created successfully');
				router.push({ name: 'edit-bulk-discount', params: { id: created.id } });
			} catch (error: any) {
				showToast(error.message || 'Failed to create bulk discount', 'error');
			} finally {
				saving.value = false;
			}
		};
		
		const editBulkDiscount = (discount: BulkModel) => {
			router.push({ name: 'edit-bulk-discount', params: { id: discount.id } });
		};
		
		const confirmDeleteBulkDiscount = async (discount: BulkModel) => {
			const result = await Swal.fire({
				title: 'Delete Bulk Discount',
				text: `Are you sure you want to delete "${discount.name}"? This action cannot be undone.`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#dc2626',
				cancelButtonColor: '#6b7280',
				confirmButtonText: 'Delete',
				cancelButtonText: 'Cancel',
				customClass: {
					popup: 'windmill-swal-popup',
				},
			});
			
			if (result.isConfirmed) {
				try {
					await BulkService.delete(discount.id);
					bulkDiscounts.value = bulkDiscounts.value.filter(d => d.id !== discount.id);
					showToast('Bulk discount deleted successfully');
				} catch (error: any) {
					showToast(error.message || 'Failed to delete bulk discount', 'error');
				}
			}
		};
		
		onMounted(() => {
			fetchData();
		});
		
		return {
			bulkDiscounts,
			loading,
			saving,
			showModal,
			searchQuery,
			currentPage,
			pageSize,
			newBulkDiscount,
			filteredBulkDiscounts,
			paginatedBulkDiscounts,
			totalPages,
			refreshData,
			openCreateModal,
			closeModal,
			createBulkDiscount,
			editBulkDiscount,
			confirmDeleteBulkDiscount,
		};
	},
});
</script>

<style scoped>
@keyframes spin {
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
}
</style>
